import React, { useCallback } from 'react';

import {
  Form,
  Pagination,
  ReferenceManyField,
  required,
  useNotify,
  usePermissions,
  useRecordContext,
  useResourceContext,
  useUpdate,
} from 'react-admin';

import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';

import LinkIcon from '@mui/icons-material/Link';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

import { AccountEntityListBody } from './AccountEntityList';
import useDialogStatus from '../../hooks/useDialogStatus';
import { IIndividual } from '../../utilities/schemas/individual';
import { IBusiness } from '../../utilities/schemas/business';
import entityTypes, {
  EntityTypes,
  KnownEntityTypes,
  entityTypesFromResource,
  resourceFromEntityTypes,
} from '../../constants/entityTypes';
import actions, { Actions } from '../../constants/actions';
import hasAccess from '../../utilities/hasAccess';
import ReferenceEntityInputV2 from '../../customFields/ReferenceEntityInputV2';
import { IAccountEntity } from '../../utilities/schemas/accountEntity';
import useContinuousRefetch from '../../hooks/useContinuousRefetch';

const RemoveConnectedAccountButton = ({
  parentId,
  parentEntityType,
  refetch,
}: {
  parentId: string;
  parentEntityType: EntityTypes;
  refetch: () => void;
}) => {
  const record = useRecordContext<IAccountEntity>();
  const notify = useNotify();

  const [removeConnectedAccount] = useUpdate('account-entities', {
    id: record?.id,
    data: {
      removeConnectedEntities: [{ id: parentId, type: parentEntityType }],
    },
  }, {
    onSuccess: () => {
      refetch();
      notify('Connected account removed successfully');
    },
    onError: () => {
      notify('Failed to remove connected account', { type: 'error' });
    },
  });

  const handleRemove = useCallback(() => {
    if (!record) return;

    removeConnectedAccount();
  }, [record, removeConnectedAccount]);

  return (
    <IconButton onClick={handleRemove}>
      <RemoveCircleOutlineIcon color="error" />
    </IconButton>
  );
};

const ConnectedAccounts = () => {
  const { open, openDialog, closeDialog } = useDialogStatus();
  const record = useRecordContext<IIndividual | IBusiness>();

  const resource = useResourceContext();

  const { refetchInterval, refetch } = useContinuousRefetch();

  const [update] = useUpdate();

  const handleSubmit = useCallback(async (data: { entity?: { id: string; type: EntityTypes } }) => {
    if (!resource || !record?.id || !data.entity) return;
    const entityType = entityTypesFromResource[resource];
    await update(resourceFromEntityTypes[data.entity.type as KnownEntityTypes], {
      id: data.entity.id,
      data: {
        addConnectedEntities: [{ id: record.id, type: entityType }],
      },
    }, {
      onSuccess: () => {
        closeDialog();
        refetch();
      },
    });
  }, [closeDialog, record?.id, refetch, resource, update]);

  const { permissions } = usePermissions<Actions[]>();
  const canUpdateAccountEntity = hasAccess(permissions, actions.ACCOUNT_ENTITY_UPDATE);

  if (!record || !resource) return null;

  return (
    <Grid container>
      {canUpdateAccountEntity && (
        <Grid size={12}>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button
              startIcon={<LinkIcon color="secondary" />}
              onClick={openDialog}
            >
              Connect account
            </Button>
            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="xs">
              <Form record={{}} onSubmit={handleSubmit}>
                <DialogTitle>Connect account</DialogTitle>
                <DialogContent>
                  <Stack spacing={4}>
                    <ReferenceEntityInputV2 source="entity" label="Account to connect" validate={required()} allowedTypes={[entityTypes.ACCOUNT]} />
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeDialog} color="secondary">
                    Cancel
                  </Button>
                  <Button type="submit" variant="contained">
                    Save
                  </Button>
                </DialogActions>
              </Form>
            </Dialog>
          </Box>
        </Grid>
      )}
      <Grid size={12}>
        <Card variant="outlined" sx={{ marginTop: 1 }}>
          <ReferenceManyField
            fullWidth
            perPage={5}
            pagination={<Pagination rowsPerPageOptions={[5, 10, 20]} />}
            reference="account-entities"
            target="connectedEntities.id"
            sort={{ field: 'createdAt', order: 'DESC' }}
            queryOptions={{ refetchInterval }}
          >
            <AccountEntityListBody>
              {canUpdateAccountEntity && (
                <RemoveConnectedAccountButton
                  parentId={record?.id}
                  parentEntityType={entityTypesFromResource[resource]}
                  refetch={refetch}
                />
              )}
            </AccountEntityListBody>
          </ReferenceManyField>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ConnectedAccounts;
